/**
 * @file: ReportTypeChooser.tsx
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * Second step of the report picker: after an order is selected, the user
 * chooses WHICH report to send. Offered types are derived from the task's
 * per-stage completion (a stage that isn't finished has no report).
 */

import { Typography } from '@euler/app/components/typography';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { CommonTaskStatus } from '@euler/model/enum';
import { ReportContentType } from '@euler/model/ReportContentType';
import { TaskListItem } from '@euler/model/TaskListItem';
import { ChevronLeft, ChevronRight } from 'lucide-react-native';
import { FC } from 'react';
import { ScrollView, TouchableOpacity, View } from 'react-native';

const REPORT_TYPE_LABELS: Record<number, string> = {
  [ReportContentType.Default]: '整体报告',
  [ReportContentType.PreInspection]: '预检报告',
  [ReportContentType.Inspection]: '检测报告',
  [ReportContentType.Construction]: '施工报告',
  [ReportContentType.DeliveryCheck]: '交车报告',
  [ReportContentType.SamplingCheck]: '抽检报告',
};

export function reportTypeLabel(t: ReportContentType): string {
  return REPORT_TYPE_LABELS[t] ?? '检测报告';
}

/** The report types worth offering for a task (always include the overall). */
export function availableReportTypes(item: TaskListItem): ReportContentType[] {
  const out: ReportContentType[] = [ReportContentType.Default];
  if (item.preInspectionStatus === CommonTaskStatus.Finished) {
    out.push(ReportContentType.PreInspection);
  }
  if (item.inspectionStatus === CommonTaskStatus.Finished) {
    out.push(ReportContentType.Inspection);
  }
  if (item.constructionStatus === CommonTaskStatus.Finished) {
    out.push(ReportContentType.Construction);
  }
  if (item.deliveryCheckStatus === CommonTaskStatus.Finished) {
    out.push(ReportContentType.DeliveryCheck);
  }
  return out;
}

export const ReportTypeChooser: FC<{
  item: TaskListItem;
  onChoose: (t: ReportContentType) => void;
  onBack: () => void;
}> = ({ item, onChoose, onBack }) => {
  const { rpx } = useResponsiveContext();
  const types = availableReportTypes(item);
  return (
    <View style={{ flex: 1 }}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(6),
          paddingHorizontal: rpx(12),
          paddingVertical: rpx(14),
        }}
      >
        <TouchableOpacity
          onPress={onBack}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <ChevronLeft size={rpx(22)} color="#111" />
        </TouchableOpacity>
        <Typography.Text
          size={rpx(16)}
          color="#111"
          style={{ fontFamily: FontFamily.NotoSans.Bold }}
        >
          选择报告类型
        </Typography.Text>
      </View>
      <Typography.Text
        size={rpx(12)}
        color="#94A3B8"
        numberOfLines={1}
        style={{ paddingHorizontal: rpx(16), marginBottom: rpx(8) }}
      >
        {[item.licensePlateNo, item.vehicleName].filter(Boolean).join(' · ')}
      </Typography.Text>
      <ScrollView contentContainerStyle={{ padding: rpx(12), gap: rpx(10) }}>
        {types.map(t => (
          <TouchableOpacity
            key={t}
            activeOpacity={0.7}
            onPress={() => onChoose(t)}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingHorizontal: rpx(16),
              paddingVertical: rpx(16),
              borderRadius: rpx(10),
              backgroundColor: '#F8FAFC',
              borderWidth: 1,
              borderColor: '#E2E8F0',
            }}
          >
            <Typography.Text
              size={rpx(15)}
              color="#1E293B"
              style={{ fontFamily: FontFamily.NotoSans.Medium }}
            >
              {reportTypeLabel(t)}
            </Typography.Text>
            <ChevronRight size={rpx(18)} color="#94A3B8" />
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );
};
